// src/features/records/components/RecordsLayout.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ViewSelector from './ViewSelector';
import SearchBar from './SearchBar';
import FilterButton from './FilterButton';
import FloatingActionButton from './FloatingActionButton';
import RecordEditor from './RecordEditor';
import RecordEditorErrorBoundary from './RecordEditorErrorBoundary';
import useSearchInputState from '../hooks/useSearchInputState';
import useFilterStore from '../stores/useFilterStore';
import useUser from '../../../hooks/useUser';
import { useMeasureToCssVar } from '../../../hooks/useMeasureToCssVar';
import styles from './RecordsLayout.module.css';

const SEARCH_DEBOUNCE_MS = 200;

const RecordsLayout = () => {
  const { t } = useTranslation();
  const { user } = useUser();
  const initialize = useFilterStore((state) => state.initialize);
  const [editorState, setEditorState] = useState(null);
  const editorKeyRef = useRef(0);
  const {
    inputValue: searchInput,
    setInputValue: setSearchInput,
    queryValue: searchQuery,
    setQueryValue: setSearchQuery,
    onCompositionStart: handleCompositionStart,
    onCompositionEnd: handleCompositionEnd
  } = useSearchInputState({ debounceMs: SEARCH_DEBOUNCE_MS });
  
  // Initialize filter store once at the parent level
  // This is the single source of truth for filter initialization
  useEffect(() => {
    if (user?.uid) {
      initialize(user.uid);
    } else {
      // User logged out - clear everything
      useFilterStore.getState().reset();
    }
  }, [user?.uid, initialize]);
  
  // Only measure the RecordsLayout's own header
  const headerRef = useRef(null);
  
  // Set CSS variable for records header height
  useMeasureToCssVar(headerRef, '--records-header-height');

  const nextEditorKey = useCallback(() => {
    editorKeyRef.current += 1;
    return editorKeyRef.current;
  }, []);

  const openEditor = useCallback((record, initialLinks) => {
    setEditorState({
      key: nextEditorKey(),
      record: record ?? null,
      initialLinks
    });
  }, [nextEditorKey]);

  const handleCreateRecord = () => {
    openEditor(null);
  };

  const handleEditRecord = (record) => {
    openEditor(record);
  };

  const handleLinkToRecord = (recordId) => {
    openEditor(null, [
      {
        targetId: recordId,
        targetType: 'record',
        addedAt: new Date()
      }
    ]);
  };

  const handleEditorClose = () => {
    setEditorState(null);
  };

  const handleSave = async (recordData) => {
    // Record is already created/updated by RecordEditor through the service layer
    // This callback is just for any additional layout-level handling (currently none needed)
    // Real-time subscriptions in views will automatically update the UI
  };

  return (
    <div className={styles.container}>
      <div ref={headerRef} className={styles.headerSection}>
        <div className={styles.searchContainer}>
          <div className={styles.searchRow}>
            <SearchBar
              value={searchInput}
              onChange={setSearchInput}
              onCompositionStart={handleCompositionStart}
              onCompositionEnd={handleCompositionEnd}
              placeholder={t('records.shell.search_placeholder')}
            />
            <FilterButton />
          </div>
        </div>
        <ViewSelector />
      </div>

      <main className={styles.mainContent}>
        <Outlet
          context={{
            searchQuery,
            setSearchQuery,
            onLinkToRecord: handleLinkToRecord,
            onEditRecord: handleEditRecord
          }}
        />
      </main>

      <FloatingActionButton onClick={handleCreateRecord} />
      
      {editorState && (
        <RecordEditorErrorBoundary
          isOpen={true}
          onClose={handleEditorClose}
          onRetry={() => {
            // Re-render the RecordEditor by resetting state
            const tempState = editorState;
            setEditorState(null);
            setTimeout(() => setEditorState({ ...tempState, key: nextEditorKey() }), 0);
          }}
          onError={(error, errorInfo) => {
            console.error('RecordEditor error caught by boundary:', error);
            // Could add error tracking/reporting here
          }}
        >
          <RecordEditor
            key={editorState.key}
            record={editorState.record}
            initialLinks={editorState.initialLinks}
            isOpen={true}
            onClose={handleEditorClose}
            onSave={handleSave}
          />
        </RecordEditorErrorBoundary>
      )}
    </div>
  );
};

export default RecordsLayout;
